import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Calendar, MessageSquare, Users, FileText, 
  Settings, LogOut, BookOpen, Menu, X, Download, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLayout = ({ children, title }) => {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const navItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: Menu },
    { name: 'Page Content', path: '/admin/content', icon: FileText },
    { name: 'Sessions', path: '/admin/sessions', icon: Calendar },
    { name: 'Blog Posts', path: '/admin/blog', icon: BookOpen },
    { name: 'Downloads', path: '/admin/downloads', icon: Download },
    { name: 'Bookings', path: '/admin/bookings', icon: BookOpen },
    { name: 'Messages', path: '/admin/messages', icon: MessageSquare },
    { name: 'Subscribers', path: '/admin/subscribers', icon: Mail },
    { name: 'Settings', path: '/admin/settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <h1 className="text-xl font-serif text-gray-900">Healing-Women Admin</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, {user?.username}</span>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-sage-300 text-sage-700 hover:bg-sage-50"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${menuOpen ? 'block' : 'hidden'} lg:block w-64 bg-white border-r border-gray-200 min-h-screen`}>
          <nav className="p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-sage-50 hover:text-sage-800 transition-colors"
              >
                <item.icon className="h-5 w-5" />
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-serif text-gray-900 mb-8">{title}</h2>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="text-center py-12">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Bookings</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.bookings?.total || 0}</p>
                <p className="text-sm text-sage-600 mt-1">{stats?.bookings?.completed || 0} completed</p>
              </div>
              <Calendar className="h-12 w-12 text-sage-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Messages</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.messages?.total || 0}</p>
                <p className="text-sm text-amber-600 mt-1">{stats?.messages?.unread || 0} unread</p>
              </div>
              <MessageSquare className="h-12 w-12 text-amber-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Subscribers</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.subscribers || 0}</p>
              </div>
              <Users className="h-12 w-12 text-lavender-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Blog Posts</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.blog_posts?.total || 0}</p>
                <p className="text-sm text-sage-600 mt-1">{stats?.blog_posts?.published || 0} published</p>
              </div>
              <FileText className="h-12 w-12 text-sage-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link to="/admin/sessions">
                <Button className="w-full justify-start bg-sage-100 text-sage-800 hover:bg-sage-200">
                  <Calendar className="mr-2 h-4 w-4" />
                  Manage Sessions
                </Button>
              </Link>
              <Link to="/admin/blog">
                <Button className="w-full justify-start bg-lavender-100 text-lavender-800 hover:bg-lavender-200">
                  <FileText className="mr-2 h-4 w-4" />
                  Create Blog Post
                </Button>
              </Link>
              <Link to="/admin/settings">
                <Button className="w-full justify-start bg-amber-100 text-amber-800 hover:bg-amber-200">
                  <Settings className="mr-2 h-4 w-4" />
                  Update Settings
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <p className="text-gray-600">Activity tracking coming soon...</p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
export { AdminLayout };
